<template>
  <lateral-modal
    id="paymentMethodsModal"
    :title="$t('company_payment_methods_settings_modal_title')"
    @close="handleClose"
  >
    <template slot="content">
      <p class="payment-methods--description">
        {{ $t('company_payment_methods_settings_description') }}
      </p>
      <div class="Fuell-Table--header">
        <div class="Fuell-Table--item">
          <span v-t="{ path: 'company_payment_methods_table_payment_method_header' }"></span>
        </div>
        <div class="Fuell-Table--item">
          <span v-t="{ path: 'company_payment_methods_table_expense_type_header' }"></span>
        </div>
        <div class="Fuell-Table--item">
          <span v-t="{ path: 'company_payment_methods_table_users_header' }"></span>
        </div>
        <div class="Fuell-Table--item">
          <span>
            {{$t('company_payment_methods_table_repayable_header')}}
            <img
              alt="info"
              v-tooltip="$t('company_payment_methods_table_repayable_tooltip')"
              :src="require('@/assets/img/icons/icon-info.svg')"
            />
          </span>
        </div>
        <div class="Fuell-Table--item">
          <span>
            {{$t('company_payment_methods_table_bydefault_header')}}
            <img
              alt="info"
              v-tooltip="$t('company_payment_methods_table_byDefault_tooltip')"
              :src="require('@/assets/img/icons/icon-info.svg')"
            />
          </span>
        </div>
        <div class="Fuell-Table--item"><span></span></div>
      </div>
      <div v-if="paymentMethodsReady">
        <div
          v-for="(paymentMethod,index) in paymentMethods"
          :key="paymentMethod.id"
          class="Fuell-Table--row"
        >
          <div class="Fuell-Table--item">
            <transition name="text-transition">
              <div
                v-if="!paymentMethod.editable"
                key="1"
                class="Fuell-Table--item--text Fuell-Text--method"
                @click="editPaymentMethod(paymentMethod)"
              >
                <p class="Fuell-Text ellipsis">
                  {{paymentMethod.name}}
                </p>
                <img
                  class="payment-methods--edit-icon"
                  alt="icon-edit"
                  :src="require('@/assets/img/icons/icon-edit.svg')"
                />
              </div>
            </transition>
            <transition name="input-transition">
              <div
                v-if="paymentMethod.editable"
                key="2"
                class="d-flex align-items-center"
              >
                <div
                  class="close-container"
                  @click="notEditPaymentMethod(paymentMethod)"
                >
                  <span class="close-line"></span>
                </div>
                <div
                  class="Form-field Form-field--method"
                  :class="{'Form--field-error' : $v.paymentMethods.$each[index].name.$error}"
                >
                  <input
                    class="Input-text"
                    type="text"
                    @blur="$v.paymentMethods.$each[index].name.$touch()"
                    v-model="paymentMethod.name"
                    :placeholder="$t('company_payment_methods_table_payment_method_name_placeholder')"
                  >
                </div>
              </div>
            </transition>
          </div>
          <div class="Fuell-Table--item Fuell-Table--item--options">
            <transition name="text-transition">
              <div
                v-if="!paymentMethod.editable"
                key="1"
                class="Fuell-Table--item--text Fuell-Text--users"
                @click="editPaymentMethod(paymentMethod)"
              >
                <p class="Fuell-Text ellipsis">
                  {{paymentMethodExpenseTypes(paymentMethod)}}</p>
                <img
                  class="payment-methods--edit-icon"
                  alt="icon-edit"
                  :src="require('@/assets/img/icons/icon-edit.svg')"
                />
              </div>
            </transition>
            <transition name="input-transition">
              <div
                v-if="paymentMethod.editable"
                key="2"
                class="Form-field--expense-type"
              >
                <multi-select-custom
                  v-model="paymentMethod.expenseTypes"
                  :options="expenseTypes"
                  :label="['name']"
                  translateLabel
                  :valueKey="'name'"
                  :placeholder="$t('company_payment_methods_table_expense_type_placeholder')"
                  @setSelectedItems="checkExpenseType(paymentMethod,$event)"
                  @removeItem="removeExpenseType(paymentMethod, $event)"
                  @close="$v.paymentMethods.$each[index].expenseTypes.$touch()"
                ></multi-select-custom>
              </div>
            </transition>
          </div>
          <div class="Fuell-Table--item Fuell-Table--item--options">
            <transition name="text-transition">
              <div
                v-if="!paymentMethod.editable"
                v-tooltip="paymentMethodUsers(paymentMethod)"
                key="1"
                class="Fuell-Table--item--text Fuell-Text--expense-type"
                @click="editPaymentMethod(paymentMethod)"
              >
                <!-- 9 horas -->
                <p class="Fuell-Text ellipsis">
                  {{paymentMethodUsers(paymentMethod)}}
                </p>
                <img
                  class="payment-methods--edit-icon"
                  alt="icon-edit"
                  :src="require('@/assets/img/icons/icon-edit.svg')"
                />
              </div>
            </transition>
            <transition name="input-transition">
              <div
                v-if="paymentMethod.editable"
                class="Form-field--users"
                key="2"
              >
                <multi-select-custom
                  v-model="paymentMethod.users"
                  :options="users"
                  :label="['firstName', 'lastName']"
                  :placeholder="$t('company_payment_methods_table_users_placeholder')"
                  :classList="'multiselect--users'"
                  :closeOnSelect="false"
                  @setSelectedItems="checkUsers(paymentMethod, $event)"
                  @removeItem="removeUser(paymentMethod,$event)"
                  @close="$v.paymentMethods.$each[index].users.$touch()"
                ></multi-select-custom>
              </div>
            </transition>
          </div>
          <div class="Fuell-Table--item">
            <div class="input-toggle--block">
              <input
                v-model="paymentMethod.repayable"
                type="checkbox"
                :true-value="true"
                :false-value="false"
                :id="`repayable${index}`"
                @change="updateRepayable(paymentMethod)"
              >
            </div>
          </div>
          <div class="Fuell-Table--item">
            <div class="input-toggle--block">
              <span v-tooltip="paymentMethod.forAllUsers ? $t('company_payment_methods_table_byDefault_disabled_tooltip') : ''">
                <input
                  v-model="paymentMethod.isDefault"
                  type="checkbox"
                  :true-value="true"
                  :false-value="false"
                  :id="`isDefault${index}`"
                  :disabled="paymentMethod.forAllUsers"
                  @change="updateDefault(paymentMethod)"
                >
              </span>
            </div>
          </div>
          <div class="Fuell-Table--item Fuell-Table--item--last">
            <transition name="text-transition">
              <button
                v-if="paymentMethod.editable"
                key="1"
                class="button-borderless text-primary clickable"
                :class="{'disabled' : $v.paymentMethods.$each[index].$invalid}"
                :disabled="$v.paymentMethods.$each[index].$invalid"
                @click="handleSavePaymentMethod(paymentMethod)"
              >{{$t('ACTION_SAVE')}}</button>
            </transition>
            <transition name="text-transition">
              <div
                v-if="!paymentMethod.editable"
                key="2"
                class="d-flex justify-content-center"
              >
                <span
                  class="Ctrl Ctrl--16 u-pointer remove-icon"
                  v-if="paymentMethods.length > 1"
                  @click="handleDeletePaymentMethod(paymentMethod)"
                >
                  <svg
                    focusable="false"
                    aria-hidden="true"
                    aria-label="Edit"
                  >
                    <use xlink:href="#close-icon"></use>
                  </svg>
                </span>
              </div>
            </transition>
          </div>
        </div>
        <button
          class="Fuell-button Fuell-button--primary p-2"
          @click="addPaymentMethod"
        >{{$t('company_payment_methods_add_payment_method_action')}}</button>
      </div>
      <div
        class="flex center"
        v-else
      >
        <spinner spinner-class-list="text-secondary"></spinner>
      </div>
    </template>
    <template slot="modals">
      <confirm-modal
        :id="'confirm-delete-modal'"
        :open.sync="showConfirmDeleteFlag"
        v-if="showConfirmDeleteFlag"
        :title="'company_payment_methods_modal_confirm_delete'"
        :message="'company_payment_methods_modal_confirm_delete_warning'"
        @accept="deletePaymentMethod"
        :acceptText="'company_payment_methods_modal_confirm_delete'"
      >
      </confirm-modal>
    </template>
  </lateral-modal>
</template>

<script>
import MultiSelectCustom from '@/components/Commons/Forms/MultiSelectCustom'
import Spinner from '@/components/Commons/Spinner'
import { EXPENSE_TYPES } from '../../_constants/paymentMethodsConstants'
import ConfirmModal from '@/components/Commons/ConfirmModal'
import officeApi from '@/modules/company/_api/'
import LateralModal from '@/components/Commons/Modals/LateralModal'

import _modalHandlerMixin from '@/components/Commons/_mixins/_modalHandlerMixin'
const { validationMixin } = require('vuelidate')
const { required, minLength } = require('vuelidate/lib/validators')

export default {
  components: { MultiSelectCustom, Spinner, ConfirmModal, LateralModal },
  data () {
    return {
      paymentMethodsReady: false,
      isSearchingUsers: false,
      showNoResults: false,
      paymentMethods: [],
      paymentMethodFakeId: 0,
      isCallingApi: false,
      showConfirmDeleteFlag: false,
      paymentMethodIdToDelete: 0
    }
  },
  mixins: [_modalHandlerMixin, validationMixin],
  computed: {
    expenseTypes () {
      return [{ name: EXPENSE_TYPES.ALL }, { name: EXPENSE_TYPES.EXPENSE }, { name: EXPENSE_TYPES.MILEAGE }, { name: EXPENSE_TYPES.DIET }]
    },
    officeId () {
      return this.$store.getters['CompanyStore/currentCompany'].id
    },
    users () {
      const fakeUserAll = {
        id: 'fake-option-all',
        firstName: this.$i18n.t('ALL'),
        lastName: '',
        code: 'ALL'
      }
      return [fakeUserAll, ...this.$store.getters['UsersStore/users']]
    }
  },
  methods: {
    notEditPaymentMethod (paymentMethod) {
      if (typeof paymentMethod.id === 'string' && paymentMethod.id.includes('pm-new')) {
        this.paymentMethods.splice(this.paymentMethods.findIndex(item => item.id === paymentMethod.id), 1)
      } else {
        this.paymentMethods.splice(this.paymentMethods.findIndex(item => item.id === paymentMethod.id), 1, paymentMethod.original)
      }
      this.$forceUpdate()
    },
    editPaymentMethod (paymentMethod) {
      if (typeof paymentMethod.id === 'string' && paymentMethod.id.includes('pm-new') && paymentMethod.editable) {
        const newPaymentMethodId = paymentMethod.id
        this.paymentMethods = this.paymentMethods.filter(el => el.id !== newPaymentMethodId)
      }
      paymentMethod.editable = !paymentMethod.editable
      if (paymentMethod.editable) {
        paymentMethod.original = Object.assign({ ...paymentMethod, editable: false })
      }

      this.$forceUpdate()
    },
    handleClose () {
      this.closeModal()
    },
    handleDeletePaymentMethod (paymentMethod) {
      this.paymentMethodIdToDelete = paymentMethod.id
      this.showConfirmDeleteFlag = true
    },
    addUser (paymentMethod, user) {
      paymentMethod.users.push(user)
    },
    async fetchUsers () {
      try {
        let userFilters = {
          'status.statusType.code[]': ['ACTIVE'],
          'roles': ['ROLE_ADMIN', 'ROLE_REGULAR', 'ROLE_ADMIN_OWNER', 'ROLE_ACCOUNT_HOLDER']
        }
        await this.$store.dispatch('UsersStore/setUsersFilters', { filters: userFilters, pagination: false })
      } catch (error) {
        this.$store.dispatch('SnackBars/addErrorHandler', { error: error, errorMessage: 'users_action_fetch_users_error' }, { root: true })
      }
    },
    async fetchPaymentMethods () {
      try {
        const response = await officeApi.getOfficePaymentMethods()
        this.paymentMethods = [...response.data]
      } catch (error) {
        this.$store.dispatch('SnackBars/addErrorHandler', { error: error, errorMessage: 'company_payment_methods_modal_get_payment_methods_error' }, { root: true })
      }
    },
    async updatePaymentMethod (paymentMethod) {
      try {
        const paymentMethodAux = { ...paymentMethod }
        if (paymentMethod.expenseTypes) {
          paymentMethodAux.expenseTypes = paymentMethodAux.expenseTypes.map(item => { return item.name })
        }
        const { id } = paymentMethodAux
        await officeApi.putOfficePaymentMethod(id, paymentMethodAux)
      } catch (error) {
        this.$store.dispatch('SnackBars/addErrorHandler', { error: error, errorMessage: 'company_payment_methods_modal_update_error' }, { root: true })
      } finally {
        paymentMethod.editable = false
        this.$forceUpdate()
      }
    },
    async createPaymentMethod (paymentMethod) {
      if (this.isCallingApi) return
      try {
        this.isCallingApi = true
        const originalPaymentMethodId = paymentMethod.id
        const newPaymentMethod = {
          ...paymentMethod
        }
        delete newPaymentMethod.id
        delete newPaymentMethod.editable
        newPaymentMethod.expenseTypes = newPaymentMethod.expenseTypes.map(item => { return item.name })
        let response = await officeApi.postOfficePaymentMethod(newPaymentMethod)
        let currentPaymentMethodIndex = this.paymentMethods.findIndex(el => el.id === originalPaymentMethodId)
        if (response.data.forAllUsers) {
          response.data.users = [...this.users.filter(el => el.code !== 'ALL')]
        }
        if (response.data.expenseTypes) {
          response.data.expenseTypes = response.data.expenseTypes.map(item => { return { name: item } })
        }
        let updatedPaymentMethod = {
          ...response.data,
          editable: false
        }
        this.paymentMethods.splice(currentPaymentMethodIndex, 1, updatedPaymentMethod)
      } catch (error) {
        console.error(error)
        this.$store.dispatch('SnackBars/addErrorHandler', { error: error, errorMessage: 'company_payment_methods_modal_create_error' }, { root: true })
      } finally {
        this.isCallingApi = false
      }
    },
    async deletePaymentMethod () {
      if (this.isCallingApi) return
      try {
        await officeApi.deleteOfficePaymentMethod(this.paymentMethodIdToDelete)
        this.paymentMethods = this.paymentMethods.filter(el => el.id !== this.paymentMethodIdToDelete)
      } catch (error) {
        this.$store.dispatch('SnackBars/addErrorHandler', { error: error, errorMessage: 'company_payment_methods_modal_delete_error' }, { root: true })
      } finally {
        this.paymentMethodIdToDelete = 0
        this.isCallingApi = false
      }
    },

    checkExpenseType (paymentMethod, event) {
      if (event && event.name === EXPENSE_TYPES.ALL) {
        paymentMethod.expenseTypes = [{ name: EXPENSE_TYPES.EXPENSE }, { name: EXPENSE_TYPES.MILEAGE }, { name: EXPENSE_TYPES.DIET }]
      } else {
        paymentMethod.expenseTypes.push(event)
      }
    },
    removeExpenseType (paymentMethod, event) {
      paymentMethod.expenseTypes = paymentMethod.expenseTypes.filter(el => el.name !== event.name)
    },
    checkUsers (paymentMethod, event) {
      if (event?.code === 'ALL') {
        paymentMethod.users = [...this.users.filter(el => el.code !== 'ALL')]
        paymentMethod.forAllUsers = true
        paymentMethod.isDefault = true
      } else {
        paymentMethod.users.push(event)
      }
      if (paymentMethod.users.length === this.users.length - 1) {
        paymentMethod.forAllUsers = true
        paymentMethod.isDefault = true
      }
    },
    removeUser (paymentMethod, event) {
      paymentMethod.users = paymentMethod.users.filter(el => el.id !== event.id)
      paymentMethod.forAllUsers = false
    },
    addPaymentMethod () {
      this.paymentMethodFakeId++
      const paymentMethod = {
        id: `pm-new-${this.paymentMethodFakeId}`,
        name: '',
        expenseTypes: [],
        users: [],
        forAllUsers: false,
        repayable: false,
        editable: true,
        isDefault: false
      }
      this.paymentMethods.push(paymentMethod)
    },
    paymentMethodUsers (paymentMethod) {
      if (paymentMethod.forAllUsers) {
        return this.$i18n.t('ALL')
      }
      return paymentMethod.users.map(el => { return `${el.firstName} ${el.lastName}` }).join(' ')
    },
    paymentMethodExpenseTypes (paymentMethod) {
      let paymentMethodExpenseTypes = paymentMethod.expenseTypes.map(el => { return this.$i18n.t(el.name) })
      const hasAllExpenseTypes = [EXPENSE_TYPES.EXPENSE, EXPENSE_TYPES.MILEAGE, EXPENSE_TYPES.DIET].every(expenseType => {
        return paymentMethod.expenseTypes.map(item => item.name).includes(expenseType)
      })
      if (hasAllExpenseTypes) {
        return this.$i18n.t('ALL')
      } else {
        return paymentMethodExpenseTypes.join(', ')
      }
    },

    handleSavePaymentMethod (paymentMethod) {
      if (typeof paymentMethod.id === 'string' && paymentMethod.id.includes('new')) {
        this.createPaymentMethod(paymentMethod)
      } else {
        this.updatePaymentMethod(paymentMethod)
      }
    },
    updateRepayable (paymentMethod) {
      if (paymentMethod.id && !paymentMethod.id.toString().includes('new')) {
        this.updatePaymentMethod({ id: paymentMethod.id, repayable: paymentMethod.repayable })
      }
    },
    updateDefault (paymentMethod) {
      if (paymentMethod.id && !paymentMethod.id.toString().includes('new')) {
        this.updatePaymentMethod({ id: paymentMethod.id, isDefault: paymentMethod.isDefault })
      }
    }
  },
  created () {
    // let filters = {
    //   'office.id': this.officeId
    // }
    let promises = [this.fetchPaymentMethods(), this.fetchUsers()]

    Promise.allSettled(promises).then(() => {
      this.paymentMethodsReady = true
      this.paymentMethods.forEach(paymentMethod => {
        paymentMethod.editable = false
        paymentMethod.expenseTypes = paymentMethod.expenseTypes.map(expenseType => { return { name: expenseType } })
        if (paymentMethod.forAllUsers) {
          paymentMethod.users = [...this.users.filter(el => el.code !== 'ALL')]
        }
      })
    })
  },
  validations: {
    paymentMethods: {
      $each: {
        name: { required },
        expenseTypes: { required, minLength: minLength(1) },
        users: { required, minLength: minLength(1) }
      }
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss">
#paymentMethodsModal {
  .Fuell-Table--row {
    position: relative;
  }
  &.ModalBox--r {
    width: 884px;
  }
  .Form-field {
    background: var(--fill-text-area);
    min-height: 32px;
    border-radius: 4px;
    margin: 0px;
    padding: 10px;
  }
  .input-toggle--block {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .Tit--modal {
    margin: 0px auto;
  }
  .payment-methods--description {
    font-weight: 400;
    font-size: 15px;
    line-height: 24px;
    color: var(--text-body);
  }
  .Fuell-Table--header,
  .Fuell-Table--row {
    grid-template-columns:
      177px 164px 161px
      140px 120px 80px;
  }
  .Fuell-Table--header {
    .Fuell-Table--item {
      font-weight: 400;
      font-size: 15px;
      line-height: 24px;
      color: var(--text-heading);

      img {
        height: 13px;
        width: 13px;
      }
    }
  }
  .Fuell-Table--row {
    margin-bottom: 8px;
    &:hover:before {
      content: none;
    }
    .Fuell-Table--item {
      font-weight: 400;
      font-size: 15px;
      line-height: 32px;
      height: 32px;
      color: var(--text-body);
      margin: 0;
      &--text {
        position: relative;
        display: flex;
        align-items: center;
        height: 32px;
        .Fuell-Text {
          margin: 0;
        }
      }
      &--options {
        overflow: unset !important;
      }
      &--last {
        display: flex;
        justify-content: flex-end;
        padding: 0 8px;
      }
      .payment-methods--edit-icon {
        display: none;
        margin-left: 7px;
      }
      &:hover {
        .payment-methods--edit-icon {
          display: inline-block;
        }
      }
    }
  }

  .multiselect-custom-field.multiselect-tags {
    .multiselect {
      min-height: 32px;
      max-height: 32px;
      height: 32px;
      .multiselect__tags,
      .multiselect__input,
      .caret {
        min-height: 32px !important;
        max-height: 32px;
        height: 32px;
      }
      &.multiselect--users {
        .multiselect__content-wrapper {
          width: 230px;
        }
      }
    }
  }
  .Form-field {
    display: flex;
    align-items: center;
    min-height: 32px;
    max-height: 32px;
    height: 32px;
    padding: 4px 12px;
  }

  .Form-field--method,
  .Fuell-Text--method {
    width: 177px;
    input {
      width: 177px;
    }
  }
  .Form-field--method {
    width: 154px;
    input {
      width: 154px;
    }
  }
  .Form-field--users,
  .Fuell-Text--users {
    width: 164px;
    .multiselect {
      width: 164px;
    }
  }
  .Form-field--expense-type,
  .Fuell-Text--expense-type {
    width: 161px;
    .multiselect {
      width: 161px;
    }
  }

  .Input-text {
    padding: 0;
    color: var(--text-body);
  }
}

.close-container {
  width: 20px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.close-line {
  width: 10px;
  height: 2px;
  background-color: var(--text-body);
  margin-right: 10px;
  cursor: pointer;
}

.remove-icon {
  display: none;
}
.Fuell-Table--row:hover {
  box-shadow: none;
  .remove-icon {
    display: flex;
  }
}
.Form--field-error {
  border: 1px solid var(--semantic-negative-main);
}
.multiselect--error {
  border: 2px solid var(--semantic-negative-main);
  border-radius: 6px;
}
.button-borderless {
  border: none;
  background-color: var(--white);
}
.disabled {
  color: var(--action-disabled) !important;
}

/* appearing */
.input-transition-enter-active {
  transition: opacity 150ms ease-out, transform 150ms ease-out;
  transform: translate(-34px, 0);
}
.text-transition-enter-active {
  transition: opacity 150ms ease-out, transform 150ms ease-out;
  transform: translate(34px, 0);
}

/* disappearing */
.input-transition-leave-active {
  transition: opacity 150ms ease-out, transform 150ms ease-out;
  transform: translate(-20px, 0);
}
.text-transition-leave-active {
  display: none;
  transition: opacity 150ms ease-out;
  opacity: 0;
}

.input-transition-enter,
.input-transition-leave-to {
  transform: translate(-34px, 0);
  opacity: 0;
}
.text-transition-enter,
.text-transition-leave-to {
  opacity: 0;
}

.input-transition-enter-to {
  position: absolute;
  transform: translate(0, 0);
}
.text-transition-enter-to {
  position: absolute;
  transform: translate(0, 0);
}
</style>
